import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface SleepGraphProps {
  data: number[];
  timeLabels: string[];
}

export function SleepGraph({ data, timeLabels }: SleepGraphProps) {
  // Generate mock stress level data that correlates with heart rate
  const stressData = data.map(hr => {
    // Convert heart rate to stress level (1-3)
    let baseStress;
    if (hr < 60) baseStress = 1; // Low stress during deep sleep
    else if (hr < 75) baseStress = 1.5; // Moderate stress during light sleep
    else if (hr < 90) baseStress = 2; // Elevated stress during REM
    else baseStress = 3; // High stress during wake periods

    // Add small random variation but keep within 1-3 range
    const noise = (Math.random() * 0.4) - 0.2;
    return Math.max(1, Math.min(3, baseStress + noise));
  });

  const chartData = {
    labels: timeLabels,
    datasets: [
      {
        label: 'Heart Rate',
        data: data,
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 1.5,
        tension: 0.4,
        pointRadius: 0,
        yAxisID: 'y',
        fill: true,
        backgroundColor: (context: any) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, context.chart.height);
          gradient.addColorStop(0, 'rgba(59, 130, 246, 0.2)');
          gradient.addColorStop(1, 'rgba(59, 130, 246, 0)');
          return gradient;
        }
      },
      {
        label: 'Stress Level',
        data: stressData,
        borderColor: 'rgba(239, 68, 68, 0.5)',
        borderWidth: 1.5,
        tension: 0.4,
        pointRadius: 0,
        yAxisID: 'y1',
        fill: true,
        backgroundColor: (context: any) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, context.chart.height);
          gradient.addColorStop(0, 'rgba(239, 68, 68, 0.1)');
          gradient.addColorStop(1, 'rgba(239, 68, 68, 0)');
          return gradient;
        }
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false
        },
        ticks: {
          color: 'rgb(156, 163, 175)',
          font: {
            size: 10
          },
          maxRotation: 0
        }
      },
      y: {
        type: 'linear' as const,
        position: 'left' as const,
        min: 40,
        max: 120,
        grid: {
          color: 'rgba(156, 163, 175, 0.1)',
          drawBorder: false
        },
        ticks: {
          color: 'rgb(156, 163, 175)',
          font: {
            size: 10
          },
          stepSize: 20
        },
        title: {
          display: true,
          text: 'Heart Rate (bpm)',
          color: 'rgb(156, 163, 175)',
          font: {
            size: 10
          }
        }
      },
      y1: {
        type: 'linear' as const,
        position: 'right' as const,
        min: 1,
        max: 3,
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          color: 'rgb(239, 68, 68)',
          font: {
            size: 10
          },
          stepSize: 1,
          callback: (value: number) => {
            return ['Low', 'Medium', 'High'][Math.floor(value) - 1];
          }
        },
        title: {
          display: true,
          text: 'Stress Level',
          color: 'rgb(239, 68, 68)',
          font: {
            size: 10
          }
        }
      }
    },
    plugins: {
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        padding: 8,
        titleFont: {
          size: 12
        },
        bodyFont: {
          size: 12
        },
        callbacks: {
          label: (context: any) => {
            if (context.dataset.label === 'Heart Rate') {
              return `Heart Rate: ${context.parsed.y} bpm`;
            }
            const stressLabels = ['Low', 'Medium', 'High'];
            const stressLevel = stressLabels[Math.floor(context.parsed.y) - 1];
            return `Stress Level: ${stressLevel}`;
          }
        }
      },
      legend: {
        display: true,
        position: 'top' as const,
        align: 'end' as const,
        labels: {
          boxWidth: 8,
          boxHeight: 8,
          padding: 15,
          color: 'rgb(156, 163, 175)',
          font: {
            size: 11
          }
        }
      }
    }
  };

  return (
    <div className="h-48 w-full">
      <Line data={chartData} options={options} />
    </div>
  );
}